import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarX, Users, Info, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { usePick } from "@/hooks/usePick";
import { todayKey, shiftKey, keyLabel, fmtDateTime, tzLabel } from "@/lib/format";
import GameCard from "@/components/GameCard";
import { DemoBadge, EmptyState, ErrorBox, PageHeader, Spinner } from "@/components/Bits";

export function LeagueSelect() {
  const { leagues, leagueId, setCurrentLeagueId } = useAuth();
  if (leagues.length < 2) return null;
  return (
    <select value={leagueId || ""} onChange={(e) => setCurrentLeagueId(e.target.value)} data-testid="league-select"
      className="rounded-full border border-white/10 bg-[#131B30] px-3 py-2 text-xs font-semibold text-white outline-none">
      {leagues.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
    </select>
  );
}

export default function Matches() {
  const { leagueId, currentLeague, tz } = useAuth();
  const [day, setDay] = useState(todayKey());
  const [data, setData] = useState(null);
  const [games, setGames] = useState([]);
  const [error, setError] = useState("");
  const { pick, saving } = usePick(setGames);

  const load = useCallback((silent = false) => {
    if (!leagueId) return;
    if (!silent) { setError(""); setData(null); }
    api.get("/games", { params: { league_id: leagueId, date: day } })
      .then(({ data }) => { setData(data); setGames(data.games.filter((g) => !g.excluded)); })
      .catch((e) => { if (!silent) setError(errMsg(e)); });
  }, [leagueId, day]);
  useEffect(() => { load(); }, [load]);
  const inPlay = games.some((g) => g.status === "live" || (g.status === "scheduled" && new Date(g.start_time) <= new Date()));
  useEffect(() => {
    if (!inPlay) return undefined;
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [inPlay, load]);

  if (!leagueId) return (<><PageHeader title="Matchs du jour" /><EmptyState icon={Users} title="Rejoignez une ligue" text="Les matchs admissibles dépendent de votre ligue." cta="Créer une ligue" to="/ligues/nouvelle" testId="matches-no-league" /></>);
  if (error) return <><PageHeader title="Matchs NBA" /><ErrorBox message={error} onRetry={() => load()} /></>;

  return (
    <div>
      <PageHeader eyebrow={currentLeague?.name} title="Matchs NBA" action={<div className="flex items-center gap-2"><Link to="/pronos-rapides" className="mpl-btn-primary text-xs" data-testid="matches-quick-link"><Zap size={14} /> Pronos rapides</Link><LeagueSelect /></div>}>
        {currentLeague?.is_demo && <DemoBadge className="mt-2" />}
      </PageHeader>
      <div className="mb-5 flex items-center justify-between gap-2 rounded-full border border-white/10 bg-[#05070D] p-1">
        <button onClick={() => setDay(shiftKey(day, -1))} className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/5" data-testid="matches-prev-day" aria-label="Jour précédent"><ChevronLeft size={18} /></button>
        <button onClick={() => setDay(todayKey())} className="text-sm font-bold capitalize" data-testid="matches-day-label">{keyLabel(day)}</button>
        <button onClick={() => setDay(shiftKey(day, 1))} className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/5" data-testid="matches-next-day" aria-label="Jour suivant"><ChevronRight size={18} /></button>
      </div>
      {currentLeague && !currentLeague.competition_started && (
        <p data-testid="matches-waiting-note" className="mb-4 flex items-start gap-2 rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-3 text-xs text-amber-200">
          <Info size={14} className="mt-0.5 shrink-0" /> Ligue en attente de joueurs (4 minimum) : vos pronostics sont enregistrés, mais les points comptent seulement une fois la compétition démarrée.
        </p>
      )}
      
      {!data && !error && <Spinner />}
      {data && (games.length ? (
        <div className="grid gap-3 md:grid-cols-2" data-testid="matches-list">
          {games.map((g) => <GameCard key={g.id} game={g} tz={tz} onPick={pick} saving={saving === g.id} setGames={setGames} rule={data.rule} />)}
        </div>
      ) : (
        <div className="space-y-3">
          <EmptyState icon={CalendarX} title="Aucun match NBA ce jour-là" text={data.next_date ? "Les prochaines rencontres arrivent bientôt." : "Aucun match à venir dans le calendrier synchronisé."} testId="matches-empty" />
          {data.next_date && <button onClick={() => setDay(data.next_date)} className="mpl-btn-primary" data-testid="matches-next-games">Voir les prochains matchs ({keyLabel(data.next_date)})</button>}
        </div>
      ))}
      {inPlay && <p data-testid="matches-live-refresh" className="mt-4 text-xs text-emerald-300">Scores en direct : mise à jour automatique toutes les 30 secondes.</p>}
      {data && !data.is_demo && !data.player_stats_available && (
        <p data-testid="matches-plan-notice" className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-xs text-slate-400">
          Questions sur les joueurs (meilleur marqueur, passeur, rebondeur) indisponibles : l'abonnement actuel au fournisseur de données ne donne pas accès aux statistiques des joueurs.
        </p>
      )}
      {data?.provider_error && (
        <p data-testid="matches-provider-error" className="mt-4 rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-3 text-xs text-amber-200">
          La source de données NBA est temporairement indisponible. Les matchs affichés proviennent de la dernière mise à jour réussie.
        </p>
      )}
      {data && (
        <p className="mt-6 text-xs text-slate-500" data-testid="matches-sync-info">
          Heures affichées : {tzLabel(tz)}. {data.last_sync_at ? `Dernière mise à jour des données : ${fmtDateTime(data.last_sync_at, tz)}.` : "Données pas encore synchronisées."}
        </p>
      )}
    </div>
  );
}
